
import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Divider,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  Google as GoogleIcon,
  GitHub as GitHubIcon,
  Microsoft as MicrosoftIcon,
} from '@mui/icons-material';
import {
  signInWithPopup,
  GoogleAuthProvider,
  GithubAuthProvider,
  OAuthProvider,
  linkWithCredential,
  fetchSignInMethodsForEmail,
  AuthProvider,
} from 'firebase/auth';
import { auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [error, setError] = useState<string>('');
  const [showError, setShowError] = useState(false);

  // アカウントリンク関連の状態
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkingInProgress, setLinkingInProgress] = useState(false);
  const [pendingCredential, setPendingCredential] = useState<any>(null);
  const [existingProviderName, setExistingProviderName] = useState<string>('');
  const [linkTargetEmail, setLinkTargetEmail] = useState<string>('');
  const [existingSignInMethods, setExistingSignInMethods] = useState<string[]>([]);

  // 現在のユーザーの認証プロバイダー情報を取得
  const getCurrentUserProviders = () => {
    if (!currentUser) return [];

    const providers = [];
    if (currentUser.providerData) {
      for (const provider of currentUser.providerData) {
        if (provider && provider.providerId) {
          providers.push(provider.providerId);
        }
      }
    }
    return providers;
  };

  // プロバイダーIDからプロバイダー名を取得
  const getProviderName = (providerId: string) => {
    switch (providerId) {
      case 'google.com':
        return 'Google';
      case 'github.com':
        return 'GitHub';
      case 'microsoft.com':
        return 'Microsoft';
      default:
        return providerId;
    }
  };

  // プロバイダーIDからアイコンを取得
  const getProviderIcon = (providerId: string) => {
    switch (providerId) {
      case 'google.com':
        return <GoogleIcon />;
      case 'github.com':
        return <GitHubIcon />;
      case 'microsoft.com':
        return <MicrosoftIcon />;
      default:
        return undefined;
    }
  };

  const handleSignIn = async (provider: GoogleAuthProvider | GithubAuthProvider | OAuthProvider) => {
    console.log('サインイン試行開始:', provider);

    try {
      const result = await signInWithPopup(auth, provider);
      console.log('サインイン成功:', result);

      // 認証成功後、ホームページにリダイレクト
      navigate('/');
    } catch (error: any) {
      console.error('ログインエラー詳細:', {
        code: error.code,
        message: error.message,
        customData: error.customData,
        email: error.customData?.email,
        stack: error.stack
      });

      // エラータイプに応じたメッセージを設定
      if (error.code === 'auth/account-exists-with-different-credential') {
        // 異なる認証プロバイダーでアカウントが存在する場合
        const email = error.customData?.email;
        const credential = error.credential;

        if (email && credential) {
          try {
            console.log('メールアドレス確認:', email);
            // 既存の認証方法を取得
            const signInMethods = await fetchSignInMethodsForEmail(auth, email);
            console.log('認証方法取得結果:', signInMethods);

            if (signInMethods.length > 0) {
              const existingProvider = signInMethods[0];
              // アカウントリンクダイアログを表示
              showAccountLinkDialog(email, existingProvider, credential, signInMethods);
              return; // エラーメッセージを表示しない
            } else {
              setError('このメールアドレスは既に登録されています。別の認証方法をお試しください。');
            }
          } catch (fetchError: any) {
            console.error('認証方法取得エラー:', fetchError);
            setError('アカウント確認中にエラーが発生しました。別の認証方法をお試しください。');
          }
        } else {
          setError('このメールアドレスは既に別の認証方法で登録されています。別の認証方法をお試しください。');
        }
      } else if (error.code === 'auth/popup-closed-by-user') {
        // ユーザーがポップアップを閉じた場合
        setError('ログインがキャンセルされました。');
      } else if (error.code === 'auth/popup-blocked') {
        // ポップアップがブロックされた場合
        setError('ポップアップがブロックされました。ブラウザの設定を確認してください。');
      } else if (error.code === 'auth/invalid-credential') {
        // 認証情報が無効な場合（Microsoft認証の設定問題など）
        setError('認証設定に問題があります。別の認証方法をお試しください。');
      } else if (error.code === 'auth/user-disabled') {
        // アカウントが無効化されている場合
        setError('このアカウントは無効化されています。管理者にお問い合わせください。');
      } else if (error.code === 'auth/user-not-found') {
        // ユーザーが見つからない場合
        setError('アカウントが見つかりません。別の認証方法をお試しください。');
      } else if (error.code === 'auth/wrong-password') {
        // パスワードが間違っている場合
        setError('認証情報が正しくありません。もう一度お試しください。');
      } else if (error.code === 'auth/too-many-requests') {
        // リクエストが多すぎる場合
        setError('リクエストが多すぎます。しばらく時間をおいてからお試しください。');
      } else if (error.code === 'auth/network-request-failed') {
        // ネットワークエラーの場合
        setError('ネットワークエラーが発生しました。インターネット接続を確認してください。');
      } else {
        // その他のエラー
        setError(`ログイン中にエラーが発生しました（${error.code}）。もう一度お試しください。`);
      }

      setShowError(true);
    }
  };

  const handleGoogleSignIn = () => {
    const provider = new GoogleAuthProvider();
    handleSignIn(provider);
  };

  const handleGitHubSignIn = () => {
    const provider = new GithubAuthProvider();
    handleSignIn(provider);
  };

  const handleMicrosoftSignIn = () => {
    const provider = new OAuthProvider('microsoft.com');
    handleSignIn(provider);
  };

  const handleCloseError = () => {
    setShowError(false);
    setError('');
  };

  // アカウントリンク処理関数
  const handleAccountLinking = async (existingProvider: string) => {
    setLinkingInProgress(true);

    try {
      let provider: AuthProvider;

      switch (existingProvider) {
        case 'google.com':
          provider = new GoogleAuthProvider();
          break;
        case 'github.com':
          provider = new GithubAuthProvider();
          break;
        case 'microsoft.com':
          provider = new OAuthProvider('microsoft.com');
          break;
        default:
          throw new Error(`サポートされていないプロバイダー: ${existingProvider}`);
      }

      // 既存のプロバイダーでサインイン
      const result = await signInWithPopup(auth, provider);
      console.log('既存プロバイダーでのサインイン成功:', result);

      // 新しい認証情報をリンク
      if (pendingCredential) {
        await linkWithCredential(result.user, pendingCredential);
        console.log('アカウントリンク成功');

        setError('アカウントの統合に成功しました。今後は複数の認証方法でサインインできます。');
        setShowError(true);

        // リンク成功後、ホームページにリダイレクト
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (linkError: any) {
      console.error('アカウントリンクエラー:', linkError);

      if (linkError.code === 'auth/credential-already-in-use') {
        setError('この認証情報は既に別のアカウントで使用されています。');
      } else if (linkError.code === 'auth/requires-recent-login') {
        setError('セキュリティのため、再度ログインが必要です。');
      } else {
        setError(`アカウントリンク中にエラーが発生しました（${linkError.code}）。もう一度お試しください。`);
      }
      setShowError(true);
    } finally {
      setLinkingInProgress(false);
      setLinkDialogOpen(false);
      setPendingCredential(null);
    }
  };

  // アカウントリンクダイアログの表示
  const showAccountLinkDialog = (email: string, existingProvider: string, credential: any, signInMethods: string[]) => {
    let providerName = '';
    switch (existingProvider) {
      case 'google.com':
        providerName = 'Google';
        break;
      case 'github.com':
        providerName = 'GitHub';
        break;
      case 'microsoft.com':
        providerName = 'Microsoft';
        break;
      default:
        providerName = existingProvider;
    }

    setLinkTargetEmail(email);
    setExistingProviderName(providerName);
    setPendingCredential(credential);
    setExistingSignInMethods(signInMethods);
    setLinkDialogOpen(true);
  };

  // 現在の認証状態を表示
  const currentProviders = getCurrentUserProviders();

  return (
    <Container component="main" maxWidth="xs">
      <Paper
        elevation={3}
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 4,
        }}
      >
        <Typography component="h1" variant="h5" gutterBottom>
          ログイン
        </Typography>

        {/* 現在の認証状態表示 */}
        {currentUser && currentProviders.length > 0 && (
          <Box sx={{ width: '100%', mb: 3 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              現在の認証方法
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {currentProviders.map((providerId) => (
                <Chip
                  key={providerId}
                  icon={getProviderIcon(providerId)}
                  label={getProviderName(providerId)}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiChip-icon': {
                      color: 'primary.main',
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Arcanaへようこそ！<br />
          アカウントにログインしてください。
        </Typography>

        <Box sx={{ width: '100%', mt: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignIn}
            sx={{ mb: 2, justifyContent: 'flex-start', padding: '10px 24px' }}
          >
            Googleでログイン
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GitHubIcon />}
            onClick={handleGitHubSignIn}
            sx={{ mb: 2, justifyContent: 'flex-start', padding: '10px 24px', color: '#333', borderColor: '#333' }}
          >
            GitHubでログイン
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<MicrosoftIcon />}
            onClick={handleMicrosoftSignIn}
            sx={{ justifyContent: 'flex-start', padding: '10px 24px', color: '#0078D4', borderColor: '#0078D4' }}
          >
            Microsoftでログイン
          </Button>
        </Box>

        <Divider sx={{ width: '100%', my: 3 }}>または</Divider>

        <Typography variant="body2" color="text.secondary" align="center">
          アカウントをお持ちでないですか？<br />
          ログインすることで自動的にアカウントが作成されます。
        </Typography>
      </Paper>

      {/* エラーメッセージ表示用のSnackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={8000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* アカウントリンクダイアログ */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => !linkingInProgress && setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          アカウントの統合
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            このメールアドレス（{linkTargetEmail}）は既に{existingProviderName}アカウントで登録されています。
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {existingProviderName}アカウントでログインすることで、新しい認証方法を既存のアカウントに追加できます。
            これにより、今後は{existingProviderName}と他の認証方法の両方で同じアカウントにログインできるようになります。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLinkDialogOpen(false)}
            disabled={linkingInProgress}
          >
            キャンセル
          </Button>
          <Button
            onClick={() => {
              const existingProvider = existingSignInMethods[0] || '';
              handleAccountLinking(existingProvider);
            }}
            variant="contained"
            disabled={linkingInProgress}
            startIcon={linkingInProgress ? <CircularProgress size={20} /> : null}
          >
            {linkingInProgress ? '統合中...' : `${existingProviderName}でログインして統合`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LoginPage;
